import { z } from "zod";

/**
 * Spot-specific, reviewed evidence. Bearing is the normal pointing from land
 * toward the sea (true north = 0°, clockwise). Never derive it from a weather
 * station, a region, a city name, or an unverified user submission.
 */
export const shorelineOrientationSchema = z.object({
  seawardBearing: z.number().finite().min(0).lt(360),
  source: z.string().url(),
  verifiedAt: z.string().datetime(),
}).strict();

export type ShorelineOrientation = z.infer<typeof shorelineOrientationSchema>;
export type WindType = "onshore" | "offshore" | "sideshore" | "unknown";

const compass = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
  "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];

/**
 * Meteorological wind direction is where wind comes FROM: wind coming from
 * the sea is onshore. Within 45° of the sea/land normal is onshore/offshore;
 * the intervening sectors, including exactly 45° and 135°, are sideshore.
 * Invalid directions or missing reviewed shoreline evidence stay unknown.
 */
export function classifyWind(windDirection: unknown, orientation: unknown): WindType {
  if (typeof windDirection !== "string") return "unknown";
  const index = compass.indexOf(windDirection.trim().toUpperCase());
  const shoreline = shorelineOrientationSchema.safeParse(orientation);
  if (index < 0 || !shoreline.success) return "unknown";
  const difference = Math.abs(
    ((index * 22.5 - shoreline.data.seawardBearing + 540) % 360) - 180,
  );
  if (difference < 45) return "onshore";
  if (difference > 135) return "offshore";
  return "sideshore";
}
